import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { TbHome2, TbLink } from 'react-icons/tb';
import classNames from 'classnames';
import { SponsorCampaignType } from '../../../types/sponsor';
import IconBadge from '../icons/IconBadge';
import Labeled from './Labeled';

interface SponsorCampaignTypePickerProps {
  value: SponsorCampaignType;
  onChange: (type: SponsorCampaignType) => void;
  label?: boolean;
}

const OPTIONS: {
  value: SponsorCampaignType;
  titleKey: string;
  hintKey: string;
  icon: typeof TbHome2;
}[] = [
  {
    value: 'COMMUNITY',
    titleKey: 'component.modal.sponsor.campaignTypeCommunity',
    hintKey: 'component.modal.sponsor.campaignTypeCommunityHint',
    icon: TbHome2,
  },
  {
    value: 'FEATURE',
    titleKey: 'component.modal.sponsor.campaignTypeFeature',
    hintKey: 'component.modal.sponsor.campaignTypeFeatureHint',
    icon: TbLink,
  },
];

export default function SponsorCampaignTypePicker({
  value,
  onChange,
  label = true,
}: SponsorCampaignTypePickerProps): JSX.Element {
  const { t } = useTranslation();

  const picker = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={classNames(
              'group relative rounded-xl border-2 p-4 text-left transition-all duration-200 hover:shadow-md',
              {
                'border-primary-900 bg-primary-900/5 shadow-sm': selected,
                'border-secondary-200 bg-secondary-200/30 hover:border-primary-900/40': !selected,
              },
            )}>
            <div
              className={classNames(
                'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all',
                {
                  'border-primary-900 bg-primary-900': selected,
                  'border-secondary-300 group-hover:border-primary-900': !selected,
                },
              )}>
              {selected && <FaCheck className="h-2.5 w-2.5 text-secondary-50" />}
            </div>

            <div className="mb-3">
              <IconBadge
                icon={option.icon}
                className={classNames({
                  'bg-primary-900 text-secondary-50': selected,
                  'bg-secondary-200 text-primary-900': !selected,
                })}
              />
            </div>

            <div className="space-y-1 pr-4">
              <h3 className="text-base font-semibold text-primary-900">{t(option.titleKey)}</h3>
              <p className="text-xs leading-relaxed text-primary-900/75">{t(option.hintKey)}</p>
            </div>
          </button>
        );
      })}
    </div>
  );

  if (!label) return picker;

  return <Labeled label={t('component.modal.sponsor.campaignType')}>{picker}</Labeled>;
}
